import { createTheme, lighten } from '@mui/material/styles';
import DelaGothicOne from '../static/DelaGothicOne-Regular.ttf';
import JakartaSansMedium from '../static/PlusJakartaSans-Medium.ttf';
import JakartaSans from '../static/PlusJakartaSans-Regular.ttf';
import { isFrontaliere } from '../utils/globalFunctions';

export const yellow = '#f8e873ff';
export const gray1 = '#333333';
export const gray2 = '#86868b';
export const gray3 = '#6E7073';
export const blue =
  process.env.REACT_APP_BRAND === 'frontaliere'
    ? '#9E1245'
    : '#52d0c6';
export const purple =
  process.env.REACT_APP_BRAND === 'frontaliere'
    ? '#FF305E'
    : '#e80d70';
export const black = '#333333';
export const white = '#ffffff';
export const blue_light = '#eaf1f4';
export const error_color = '#D32F2F';

export const frontaliere_rose = '#FF305E';
export const frontaliere_orange = '#FFAE8B';
export const frontaliere_prune = '#9E1245';
export const frontaliere_cerise = '#4F0A14';

export const breakpoints = {
  values: {
    xs: 0,
    ss: 400,
    sm: 768,
    md: 1024,
    lg: 1200,
    xl: 1600,
  },
};

const theme = createTheme({ breakpoints });

const isAppleDevice = navigator.vendor
  .toLowerCase()
  .includes('apple');
export const frontaliereThemeCore = {
  breakpoints,
  palette: {
    primary: {
      main: '#000',
      contrastText: '#fff',
    },
    secondary: {
      main: blue,
    },
    purple: {
      main: purple,
      contrastText: '#fff',
    },
    selected: frontaliere_prune,
    hovered: lighten(frontaliere_orange, 0.8),
    hoverBlue: '#52d0c625',
    button: {
      main: frontaliere_rose,
      secondary: frontaliere_prune,
      contrastText: '#ffffff',
    },
    background: {
      default: '#fff',
    },
    text: {
      main: gray1,
    },
    affiliate: {
      main: '#00891E',
    },
    preaffiliate: {
      main: '#F5C234',
    },
    error: {
      main: '#D32F2F',
    },
  },
  typography: {
    fontFamily: 'JakartaSans',
    color: '#333333',

    h1: {
      fontFamily: 'DelaGothicOne',
      color: '#333333',
      zIndex: 100,
      fontSize: '40px !important',
      lineHeight: '48px',
      [theme.breakpoints.down('xl')]: {
        fontSize: '40px !important',
        lineHeight: '48px',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '28px !important',
        lineHeight: '38px',
      },
    },
    h2: {
      fontFamily: 'DelaGothicOne',
      color: '#333333',
      zIndex: 100,
      fontSize: '32px !important',
      [theme.breakpoints.down('xl')]: {
        fontSize: '22px !important',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '18px !important',
      },
    },
    h3: {
      fontFamily: 'DelaGothicOne',
      color: '#333333',
      zIndex: 100,
      fontSize: '2rem',
      [theme.breakpoints.down('xl')]: {
        fontSize: '1.7rem',
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '1.5rem',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.3rem',
      },
    },
    h4: {
      zIndex: 100,
      lineHeight: 1.5,
      color: '#333333',
      fontSize: '1.625rem',
      [theme.breakpoints.down('md')]: {
        fontSize: '1.325rem',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.125rem',
      },
    },
    h5: {
      zIndex: 100,
      color: '#333333',
      fontSize: '16px !important',
      [theme.breakpoints.down('xl')]: {
        // fontSize: '13px !important'
      },
    },
    h6: {
      zIndex: 100,
      color: '#333333',
      fontSize: '20px !important',
      // lineHeight:60
      [theme.breakpoints.down('md')]: {
        fontSize: '14px !important',
      },
    },
    p: {
      color: '#333333',
    },
    nav: {
      fontFamily: 'JakartaSansMedium !important',
    },
  },

  components: {
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          '& .Mui-selected': {
            backgroundColor: `${blue} !important`,
          },
          '& .PrivatePickersYear-root button:hover': {
            backgroundColor: lighten(frontaliere_orange, 0.7),
          },
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
        },
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        root: {
          button: {
            color: 'black',
            background: 'none',
            border: 'none',
          },
          '& .MuiTypography-root:hover': {
            backgroundColor: lighten(frontaliere_orange, 0.7),
          },
          '& .Mui-selected': {
            backgroundColor: `${blue} !important`,
          },
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekContainer: {
          '& .Mui-selected': {
            backgroundColor: `${blue} !important`,
          },
          '& .MuiPickersDay-root:hover': {
            backgroundColor: lighten(frontaliere_orange, 0.7),
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {},
        labelContainer: {
          ' .MuiButtonBase-root': {
            display: 'none',
          },
        },
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        root: {
          display: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '& .Mui-selected': {
            backgroundColor: `${lighten(frontaliere_orange, 0.4)} !important`,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        colorPrimary: {
          color: frontaliere_prune,
          '&.Mui-checked': {
            color: frontaliere_prune,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '100px !important',
          backgroundColor: 'white',
          borderColor: frontaliere_cerise,
          textAlign: 'left',
          '&: .MuiList-root': {
            backgroundColor: 'white',
          },
        },
        select: {
          whiteSpace: 'pre-wrap',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: '100px !important',
          backgroundColor: 'white',
          variants: [
            {
              props: { variant: 'outlined' },
              style: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: '100px',
                  // border: '0.5px solid',
                  borderColor: frontaliere_cerise,
                  backgroundColor: 'white',
                },
              },
            },
          ],
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.active': {
            backgroundColor: `${lighten(frontaliere_orange, 0.6)} !important`,
          },
          '&.hover': {
            backgroundColor: `${lighten(frontaliere_orange, 0.85)} !important`,
          },
          '&:not(.active):hover': {
            backgroundColor: `${lighten(frontaliere_orange, 0.85)} !important`,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: lighten(frontaliere_orange, 0.7),
          },
          '&.Mui-selected,&.Mui-selected.Mui-focusVisible, &.Mui-selected:hover':
            {
              backgroundColor: frontaliere_orange,
            },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          backgroundColor: lighten(frontaliere_rose, 0.9),
          textTransform: 'none',
          '&.Mui-selected': {
            backgroundColor: frontaliere_rose,
            color: 'white',
            border: 'none',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          width: 'fit-content',
          margin: 'auto',
          borderRadius: '100px !important',
          boxShadow: 'none',
          color: '#fffff',
          fontSize: '16px',
          [theme.breakpoints.down('md')]: {
            fontSize: 12,
          },
          fontWeight: 'initial !important',
          textTransform: 'none',
          '&.Mui-disabled': {
            backgroundColor: '#ECD0DA !important',
          },
          '&:hover': {
            backgroundColor: 'white',
            borderColor: frontaliere_rose,
            color: frontaliere_rose,
            border: '1px solid',
          },
          // '&:focus': {
          //   backgroundColor: frontaliere_cerise,
          //   color: 'white',
          // },
          variants: [
            {
              props: { variant: 'outlined' },
              style: {
                backgroundColor: 'white',
                color: `${frontaliere_rose} !important`,
                border: '1px solid',
                borderColor: frontaliere_rose,
                '&:hover': {
                  backgroundColor: frontaliere_rose,
                  color: 'white !important',
                },
                '&:active': {
                  backgroundColor: frontaliere_cerise,
                  color: 'white',
                },
              },
            },
            {
              props: { variant: 'secondary' },
              style: {
                backgroundColor: `${frontaliere_prune} !important`,
                color: 'white',
                border: '1px solid transparent',
                '&:hover': {
                  backgroundColor: 'white !important',
                  borderColor: frontaliere_prune,
                  color: frontaliere_prune,
                },
                '&:active': {
                  backgroundColor: frontaliere_cerise,
                  color: 'white',
                },
              },
            },
            {
              props: { variant: 'secondary_outlined' },
              style: {
                backgroundColor: 'white',
                color: frontaliere_prune,
                border: '1px solid',
                '&:hover': {
                  backgroundColor: frontaliere_prune,
                  color: 'white',
                },
                '&:active': {
                  backgroundColor: frontaliere_cerise,
                  color: 'white',
                },
              },
            },
          ],
        },
      },
    },
    MuiStack: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: `${frontaliere_cerise} !important`,
          borderRadius: '100px !important',
          '& .MuiTableRow-root': {
            '& .MuiTableCell-root': {
              color: 'white',
              '&:first-child': {
                borderTopLeftRadius: '20px',
              },
              '&:last-child': {
                borderTopRightRadius: '20px',
              },
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { color: gray1 },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            textAlign: 'center',
            '&:last-child': {
              textAlign: 'right',
            },
          },
          backgroundColor: 'transparent !important',
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        thumb: {
          backgroundColor: '#fff',
        },
        track: {
          // Controls default (unchecked) color for the track
          opacity: 0.2,
          backgroundColor: '#000',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: blue,
        },
        standardSuccess: {
          backgroundColor: blue,
        },
        standardError: {
          backgroundColor: blue,
        },
        message: {
          color: '#fff',
          '& *': {
            color: '#fff',
          },
        },
        icon: {
          '& *': {
            color: '#fff',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '1rem',
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: gray1,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          textAlign: 'center',
          pt: 3,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          pb: 3,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          color: gray1,
          '& .MuiPaper-root': {
            backgroundColor: 'white !important',
            borderRadius: '20px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          height: '100%',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          ...(isFrontaliere && {
            '& .MuiCardHeader-title': {
              fontSize: '24px ',
              color: '#333',
            },
          }),
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        circle: {
          color: purple,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          '&:after': {
            borderColor: blue,
          },
          '&:hover:not(.Mui-disabled):before': {
            borderColor: blue,
          },
          [theme.breakpoints.down('xl')]: {
            fontSize: '16px !important',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
            body {
              background-color: #FFEFE8;
            }
            @font-face {
                font-family: 'DelaGothicOne';
                font-style: normal;
                font-display: swap;
                font-weight: ${isAppleDevice ? '900' : '1100'};
                src: local('DelaGothicOne'), local('DelaGothicOne'), url(${DelaGothicOne}) format('woff2');
                unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;  
             }
            @font-face {
                font-family: 'JakartaSans';
                font-style: normal;
                font-display: swap;
                font-weight: ${isAppleDevice ? '900' : '1100'};
                src: local('JakartaSans'), local('JakartaSans'), url(${JakartaSans}) format('woff2');
                unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;  
             }
                 @font-face {
                font-family: 'JakartaSansMedium';
                font-style: normal;
                font-display: swap;
                font-weight: ${isAppleDevice ? '900' : '1100'};
                src: local('JakartaSansMedium'), local('JakartaSansMedium'), url(${JakartaSansMedium}) format('woff2');
                unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;  
             }`,
    },
  },
};
